import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { SideMenuDotsContext } from '~/components/layouts/MainLayout/SideMenuDotsContext'
import { useAutoBidNotOrderedNotices } from '~/hooks/useAutoBidNotOrderedNotices'
import { useUnknownIssuerBidMarkets } from '~/hooks/useUnknownIssuerBidMarkets'
import { autoBidNotOrderedDots } from '~/models/autoBidNotOrderedDot'
import { collectSideMenuDots } from '~/models/sideMenuDot'
import { unknownIssuerBidDots } from '~/models/unknownIssuerBid'

/**
 * サイドメニューのドットの取得と「メニュー項目の path → ドット」の組み立てを
 * layout の 1 か所に置き、context で配る。
 *
 * react-query の `refetchInterval` は observer ごとなので、hover の popup や副パネルが
 * それぞれ取得すると 30 秒に複数回になる。DS が保持する node (副パネル・popup) にも
 * context なら届く。
 *
 * ドットの種類を足す時は `collectSideMenuDots` の配列に「作る側」を足すだけにする
 * (AC-SC-004 / 005 / 034 / 035 / 042。同じ項目に重なれば 1 つにまとまる)。
 */
export const SideMenuDotsProvider = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => {
  const { t } = useTranslation()
  const { notices } = useAutoBidNotOrderedNotices()
  const { markets: unknownIssuerBidMarkets } = useUnknownIssuerBidMarkets()
  const autoBidReason = t('common:nav.autoBidNotOrdered')
  const unknownIssuerBidReason = t('common:nav.unknownIssuerBid')

  const value = useMemo(
    () => ({
      dots: collectSideMenuDots([
        ...autoBidNotOrderedDots(notices, autoBidReason),
        ...unknownIssuerBidDots(
          unknownIssuerBidMarkets,
          unknownIssuerBidReason,
        ),
      ]),
      autoBidNotOrderedNotices: notices,
    }),
    [notices, autoBidReason, unknownIssuerBidMarkets, unknownIssuerBidReason],
  )

  return (
    <SideMenuDotsContext.Provider value={value}>
      {children}
    </SideMenuDotsContext.Provider>
  )
}
